/**
 * A refusal from /api/auth/*, as a sentence in the language the page is in.
 *
 * The server answers with an i18n key — `auth.err.credentials`, or
 * `auth.err.locked:15` where the number after the colon is the argument.
 * That is on purpose (platform/accounts.ts): a Chinese page must not be told
 * what to say in English, and a key travels without a language.
 *
 * Anything this table does not know becomes the general "that did not go
 * through". A page that printed an unrecognised key at somebody would be
 * showing them the inside of the program.
 */
import { fmt, t, type Lang, type Key } from "@/lib/i18n/index";

export function messageFor(lang: Lang, error: string | null | undefined): string {
  const s = t(lang);
  const [key, arg] = String(error ?? "").split(":");
  const known = key in s ? (key as Key) : null;
  if (!known || !known.startsWith("auth.err.")) return s["auth.err.failed"];
  // The argument is a number or it is nothing. Since the Google callback,
  // one of the callers of this is the address bar (`/login?error=…`), and
  // `auth.err.locked:<anything>` would otherwise put whatever somebody put
  // in a link into a sentence on the real sign-in page, under the real
  // domain — not a script, but a very good lie.
  const said = s[known];
  // A key that takes an argument and did not get one is not a sentence. It
  // used to be printed as it stands, so the real sign-in page said
  // 「失败次数太多，**%1** 分钟后再试。」 — the placeholder shown to the
  // person, from a link somebody else wrote. Nothing to say is the general
  // refusal, which is true and is a sentence.
  if (/^\d{1,4}$/.test(arg ?? "")) return fmt(said, arg);
  return said.includes("%1") ? s["auth.err.failed"] : said;
}
